import { Suspense, lazy, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Users, Handshake, Landmark, Sparkles, Globe2, Loader2, X,
  ArrowUpRight, MapPin, Layers, HeartHandshake, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import AnimatedCounter from "@/components/AnimatedCounter";
import { FeatureErrorBoundary } from "@/components/AppErrorBoundary";
import { lazyWithRecovery } from "@/lib/lazyWithRecovery";
import {
  SectionHeading, NewsCard, BlogCard, OrgCard, OpportunityCard, EventCard, InitiativeCard,
  GoldButton, OutlineButton, Pill, CardSkeleton,
} from "@/components/Cards";

const Globe3D = lazy(lazyWithRecovery(() => import("@/components/Globe3D"), "globe"));

const PILLARS = [
  { icon: Users, title: "Connect", desc: "Build relationships across communities and continents through verified chapters and diaspora networks." },
  { icon: HeartHandshake, title: "Empower", desc: "Support professionals, entrepreneurs, youth and community leaders with mentorship and opportunity." },
  { icon: ShieldCheck, title: "Preserve", desc: "Protect history, culture, identity and heritage for future generations." },
  { icon: Landmark, title: "Build", desc: "Create partnerships, initiatives and institutions that strengthen communities worldwide." },
];

function useData(path, deps = []) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get(path).then(({ data }) => alive && setData(data)).catch(() => alive && setData([]));
    return () => { alive = false; };
    // eslint-disable-next-line
  }, deps);
  return data;
}

export default function Home() {
  const navigate = useNavigate();
  const settings = useData("/site-settings");
  const locations = useData("/locations");
  const connections = useData("/connections");
  const regions = useData("/regions");
  const news = useData("/news?limit=3");
  const initiatives = useData("/initiatives?limit=4");
  const orgs = useData("/organizations?limit=3");
  const opps = useData("/opportunities?limit=3");
  const blogs = useData("/blogs?limit=3");
  const events = useData("/events?limit=3");
  const siteConfig = useData("/site-config");
  const [selected, setSelected] = useState(null);

  const heroStats = settings?.hero_stats || [];
  const impactStats = settings?.impact_stats || [];
  const homepageSections = siteConfig?.homepage?.sections || [];
  const sectionProps = (type) => {
    const index = homepageSections.findIndex((s) => s.type === type);
    const section = index >= 0 ? homepageSections[index] : null;
    return { style: { order: index >= 0 ? index : 99 }, hidden: section?.visible === false };
  };

  return (
    <div className="flex flex-col bg-dir-bg">
      {/* HERO */}
      <section {...sectionProps("hero")} className="relative h-[100svh] min-h-[640px] w-full overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-y-0 right-0 w-full bg-dir-bg lg:w-[76%]">
          <FeatureErrorBoundary name="Homepage Globe" fallback={<div className="flex h-full items-center justify-center p-8 text-center text-dir-muted">The interactive globe is temporarily unavailable. All DIR Nation content remains accessible below.</div>}><Suspense fallback={<div className="flex h-full items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-dir-gold" /></div>}>
            {locations && connections && <Globe3D locations={locations} regions={regions || []} connections={connections} onSelect={setSelected} />}
          </Suspense></FeatureErrorBoundary>
        </div>
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-dir-bg via-dir-bg/65 to-transparent lg:via-dir-bg/45" />
        <div className="pointer-events-none absolute inset-0 hero-vignette" />

        <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-4 sm:px-6 lg:px-10">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="max-w-2xl">
            <Pill testId="hero-badge">{settings?.hero_badge || "The Global Dir Community"}</Pill>
            <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-dir-text sm:text-5xl lg:text-6xl" data-testid="hero-title">
              {settings?.hero_title || "Connecting 15 Million+ Dir People Across the World"}
            </h1>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-dir-muted sm:text-lg">
              {settings?.hero_subtitle}
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <GoldButton data-testid="hero-join-cta" onClick={() => navigate("/signup")}>Join DIR Nation <ArrowRight className="h-4 w-4" /></GoldButton>
              <OutlineButton data-testid="hero-explore-cta" onClick={() => navigate("/global-network")}>Explore the Global Network</OutlineButton>
            </div>
          </motion.div>
        </div>

        {/* Live stats HUD */}
        {heroStats.length > 0 && (
          <div className="absolute bottom-0 inset-x-0 z-10 border-t border-dir-gold/15 glass">
            <div className="mx-auto grid max-w-[1400px] grid-cols-2 divide-x divide-dir-gold/10 px-4 sm:px-6 lg:px-10 md:grid-cols-4">
              {heroStats.map((s, i) => (
                <div key={i} className="px-4 py-5 text-center md:text-left" data-testid={`hero-stat-${i}`}>
                  <div className="font-display text-2xl font-bold text-gradient-gold sm:text-3xl">
                    <AnimatedCounter value={s.value} />
                  </div>
                  <div className="mt-1 text-[11px] uppercase tracking-wider text-dir-muted">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Location modal */}
      {selected && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={() => setSelected(null)} data-testid="location-modal">
          <div className="w-full max-w-md rounded-2xl glass p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setSelected(null)} className="float-right text-dir-muted hover:text-dir-gold" data-testid="location-modal-close"><X className="h-5 w-5" /></button>
            <Pill>{selected.region}</Pill>
            <h3 className="mt-4 font-display text-2xl font-bold text-dir-text">{selected.name}</h3>
            <p className="text-sm text-dir-gold">{selected.country}</p>
            <p className="mt-4 text-sm text-dir-muted">{selected.description}</p>
            <dl className="mt-6 space-y-2 text-sm">
              <div className="flex justify-between border-b border-dir-gold/10 pb-2"><dt className="text-dir-faint">Community</dt><dd className="text-dir-text">{selected.community}</dd></div>
              <div className="flex justify-between border-b border-dir-gold/10 pb-2"><dt className="text-dir-faint">Population</dt><dd className="text-dir-text">{selected.population}</dd></div>
              <div className="flex justify-between"><dt className="text-dir-faint">Status</dt><dd className="text-dir-gold">{selected.status}</dd></div>
            </dl>
            <div className="mt-6 flex gap-3">
              <GoldButton className="flex-1" onClick={() => navigate("/global-network")}>Explore Community</GoldButton>
            </div>
          </div>
        </div>
      )}

      {/* PILLARS */}
      <Section {...sectionProps("pillars")} testId="pillars-section">
        <SectionHeading center label="One People. A Global Network." title="Uniting a global community" desc="DIR Nation brings together communities across continents through a shared platform for connection, knowledge, opportunity and development." />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {PILLARS.map((p, i) => (
            <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="group rounded-2xl border border-dir-gold/15 bg-dir-card p-8 hover:border-dir-gold/50 transition-all" data-testid={`pillar-${p.title.toLowerCase()}`}>
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-dir-gold/30 bg-dir-gold/10">
                <p.icon className="h-6 w-6 text-dir-gold" />
              </div>
              <h3 className="mt-5 font-display text-xl font-semibold text-dir-text">{p.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-dir-muted">{p.desc}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* REGIONS */}
      <Section {...sectionProps("regions")} muted testId="regions-section">
        <SectionHeading label="Our Global Community" title="A network spanning every continent" desc="Explore active DIR Nation chapters and diaspora communities across the world." />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {(regions || []).map((r, i) => (
            <Link key={r.region} to="/global-network" data-testid={`region-card-${i}`} className="group relative overflow-hidden rounded-2xl border border-dir-gold/15 bg-dir-card p-8 hover:border-dir-gold/50 transition-all">
              <Globe2 className="absolute -right-6 -top-6 h-28 w-28 text-dir-gold/5" />
              <div className="relative">
                <h3 className="font-display text-xl font-semibold text-dir-text group-hover:text-dir-gold transition-colors">{r.region}</h3>
                <p className="mt-2 text-sm text-dir-muted">{r.count} communities · {r.countries.slice(0, 3).join(", ")}</p>
                <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-dir-gold">Explore region <ArrowUpRight className="h-4 w-4" /></div>
              </div>
            </Link>
          ))}
        </div>
      </Section>

      {/* NEWS */}
      <Section {...sectionProps("news")} testId="home-news-section">
        <div className="flex items-end justify-between">
          <SectionHeading label="Latest News" title="Global Knowledge Hub" />
          <Link to="/news" className="mb-12 hidden sm:inline-flex items-center gap-1 text-sm font-medium text-dir-gold" data-testid="view-all-news">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <Grid data={news} render={(n) => <NewsCard key={n.id} item={n} />} />
      </Section>

      {/* INITIATIVES */}
      <Section {...sectionProps("initiatives")} muted testId="home-initiatives-section">
        <SectionHeading label="Building Our Future Together" title="Featured Initiatives" desc="Community-led projects driving education, development, culture and opportunity." />
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {(initiatives || []).map((n) => <InitiativeCard key={n.id} item={n} />)}
        </div>
      </Section>

      {/* ORGS + OPPS */}
      <Section {...sectionProps("organizations")} testId="home-orgs-section">
        <div className="grid gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading label="Organizations & Institutions" title="Verified directory" />
            <div className="space-y-6">{(orgs || []).map((n) => <OrgCard key={n.id} item={n} />)}</div>
            <Link to="/organizations" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-dir-gold">All organizations <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <div>
            <SectionHeading label="Opportunities Hub" title="Jobs, grants & scholarships" />
            <div className="space-y-6">{(opps || []).map((n) => <OpportunityCard key={n.id} item={n} />)}</div>
            <Link to="/opportunities" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-dir-gold">All opportunities <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </Section>

      {/* BLOGS */}
      <Section {...sectionProps("blogs")} muted testId="home-blogs-section">
        <div className="flex items-end justify-between">
          <SectionHeading label="Insights & Blogs" title="Perspectives from our community" />
          <Link to="/blogs" className="mb-12 hidden sm:inline-flex items-center gap-1 text-sm font-medium text-dir-gold">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <Grid data={blogs} render={(n) => <BlogCard key={n.id} item={n} />} />
      </Section>

      {/* EVENTS */}
      <Section {...sectionProps("events")} testId="home-events-section">
        <SectionHeading label="Events & Summits" title="Upcoming events" />
        <Grid data={events} render={(n) => <EventCard key={n.id} item={n} />} />
      </Section>

      {/* IMPACT STATS */}
      <section {...sectionProps("impact")} className="relative overflow-hidden border-y border-dir-gold/15 bg-dir-deep py-20 section-noise" data-testid="impact-stats-section">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
          <div className="grid gap-8 text-center sm:grid-cols-2 lg:grid-cols-4">
            {impactStats.map((s, i) => (
              <div key={i} data-testid={`impact-stat-${i}`}>
                <div className="font-display text-3xl font-bold text-gradient-gold sm:text-4xl lg:text-5xl">
                  <AnimatedCounter value={s.value} />
                </div>
                <div className="mt-2 text-sm uppercase tracking-wider text-dir-muted">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOIN CTA */}
      <section {...sectionProps("join")} className="relative overflow-hidden bg-dir-green py-24" data-testid="join-cta-section">
        <div className="absolute inset-0 section-noise opacity-60" />
        <Globe2 className="absolute -right-20 top-1/2 h-[420px] w-[420px] -translate-y-1/2 text-dir-gold/5" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <Sparkles className="mx-auto h-8 w-8 text-dir-gold" />
          <h2 className="mt-6 font-display text-3xl font-bold text-dir-text sm:text-4xl lg:text-5xl">Be Part of a Global Community</h2>
          <p className="mx-auto mt-5 max-w-xl text-base leading-relaxed text-dir-muted">
            Connect with Dir professionals, organizations, communities and leaders around the world. Discover opportunities, share knowledge and contribute to the future of our global community.
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-4">
            <GoldButton onClick={() => navigate("/signup")} data-testid="cta-join">Join DIR Nation <ArrowRight className="h-4 w-4" /></GoldButton>
            <OutlineButton onClick={() => navigate("/global-network")} data-testid="cta-explore">Explore Our Global Network</OutlineButton>
          </div>
        </div>
      </section>
    </div>
  );
}

function Section({ children, muted, testId, style, hidden }) {
  return (
    <section data-testid={testId} style={style} hidden={hidden} className={`py-16 md:py-24 ${muted ? "bg-dir-deep" : ""}`}>
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">{children}</div>
    </section>
  );
}

function Grid({ data, render }) {
  if (!data) return <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((i) => <CardSkeleton key={i} />)}</div>;
  return <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.map(render)}</div>;
}
